import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
// Campaign Model v2 (Phase A migration 20260421140000): each campaign and
// each segment can carry its own angles[] / offers[] JSONB. See
// src/lib/campaign-angles-offers.ts for resolver semantics.
import type { AngleOfferEntry } from '@/lib/campaign-angles-offers'

export interface Campaign {
  id: string
  name: string
  status: string
  client_id: string
  campaign_type: string
  description?: string
  campaign_planner_notes?: string | null
  version?: string
  trigger_type?: string
  campaign_purpose?: string
  previous_campaign_id?: string
  channels?: string[]
  target_regions?: string[]
  clay_table_link?: string
  clickup_task_link?: string
  copywriting_doc_link?: string
  smartlead_link?: string
  heyreach_link?: string
  linkedin_sequence_link?: string
  tam_list_file_name?: string
  tam_list_file_url?: string
  created_at: string
  updated_at: string
  launched_at?: string
  completed_at?: string
  /** Teammate-set planned go-live date. Surfaces on the dashboard's
   *  Upcoming Campaigns section. Null = TBD. Distinct from
   *  `launched_at`, which is set automatically when the campaign goes
   *  live. Added 2026-05-07 (Sabeeh). */
  planned_launch_date?: string | null
  // Admin pinned status — wins over derive_campaign_status() (Sabeeh 2026-05-07).
  manual_status_override?: string | null
  manual_status_override_by?: string | null
  manual_status_override_at?: string | null
  manual_status_override_reason?: string | null
  // Campaign Model v2 — used as fallback when a campaign has 0 segments
  // (Duel V1, Cruva V2, StoryStream V3 after relabel). Always defined
  // server-side (DEFAULT '[]'::jsonb), but optional in TS for
  // back-compat with existing call sites that don't request these fields.
  angles?: AngleOfferEntry[]
  offers?: AngleOfferEntry[]
  // Phase H — region + subject_line dimensions. Regions are campaign-only;
  // subject_lines are hybrid (campaign-level falls back when segment
  // subject_lines is empty).
  regions?: AngleOfferEntry[]
  subject_lines?: AngleOfferEntry[]
  // Slack launch-announcement support (added 2026-06-02, Jamie-approved).
  // event_date only set for campaign_type='event'. announcement_meta is a
  // JSONB bag read by buildCampaignAnnouncement() — see src/lib/campaign-announcement.ts.
  event_date?: string | null
  announcement_meta?: CampaignAnnouncementMeta | null
}

/** JSONB shape stored in campaigns.announcement_meta. All optional —
 *  the generator falls back gracefully when a field is missing. */
export interface CampaignAnnouncementMeta {
  goal?: string
  target_metric?: string
  icp_titles?: string
  icp_examples?: string
  icp_criteria?: string
  channels?: string[]
  offer_summary?: string
}

export interface CampaignSegment {
  id: string
  campaign_id: string
  name: string
  status: string
  purpose?: string
  smartlead_link?: string
  heyreach_link?: string
  created_at: string
  updated_at: string
  // Campaign Model v2 — per-segment angles/offers (preferred over
  // campaign-level when the segment exists).
  angles?: AngleOfferEntry[]
  offers?: AngleOfferEntry[]
  // Phase H — per-segment subject_lines (hybrid with campaign-level).
  // Regions are campaign-only; no per-segment regions field.
  subject_lines?: AngleOfferEntry[]
}

export interface CampaignTamLead {
  id: string
  campaign_id: string
  email: string
  first_name?: string
  last_name?: string
  company_name?: string
  job_title?: string
  linkedin_url?: string
  phone?: string
  additional_data: any
  created_at: string
}

/**
 * @param filterClientId - Filter by client_id.
 *   undefined = fetch all campaigns (admin "all clients" view)
 *   null = skip fetching (workspace still loading — prevents unfiltered data leakage)
 *   string = fetch filtered by client_id
 */
export function useCampaigns(includeArchived: boolean = false, filterClientId?: string | null) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const { toast } = useToast()

  /** PostgREST returns this exact wording for ~1–3s after any DDL change.
   * Auto-retries should swallow it instead of toasting at the user. */
  const isSchemaCacheTransient = (msg: string | undefined): boolean =>
    !!msg && /schema cache|Retrying/i.test(msg)

  const fetchCampaigns = async (attempt = 0): Promise<void> => {
    // null means workspace is still loading — don't fetch to prevent data leakage
    if (filterClientId === null) return

    setLoading(true)
    try {
      // Always query real Supabase data — campaigns table is populated by edge functions,
      // not dependent on SmartLead API key being present in the browser.
      let query = supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false })

      // Only exclude archived campaigns if includeArchived is false
      if (!includeArchived) {
        query = query.neq('status', 'archived')
      }

      // Filter by client_id when scoped to a specific client
      if (filterClientId) {
        query = query.eq('client_id', filterClientId)
      }

      const { data, error } = await query

      if (error) throw error
      setCampaigns(data || [])
      setError('')
    } catch (err: any) {
      const msg: string = err?.message ?? String(err)

      // PostgREST schema-cache reload is a transient hiccup — silently retry
      // up to 2 times with exponential-ish backoff before showing a toast.
      if (isSchemaCacheTransient(msg) && attempt < 2) {
        const delay = 600 * (attempt + 1)
        setTimeout(() => {
          // No await; intentional fire-and-forget so loading state doesn't flicker.
          void fetchCampaigns(attempt + 1)
        }, delay)
        return
      }

      setError(msg)
      // Don't toast for the transient case even after retries exhausted — log only.
      if (isSchemaCacheTransient(msg)) {
        console.warn('[useCampaigns] schema cache still reloading after retries:', msg)
      } else {
        toast({
          title: 'Error fetching campaigns',
          description: msg,
          variant: 'destructive',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  // Campaign Model v2 (Phase C + Phase H): structure passed by the wizard
  // form. Used to auto-create segments + variants in a single
  // createCampaign call. 5-D cell = segment × angle × offer × region × subject_line.
  interface SegmentStructure {
    name: string
    description?: string
    angles: AngleOfferEntry[]
    offers: AngleOfferEntry[]
    /** Phase H: per-segment subject_lines (hybrid with campaign-level). */
    subject_lines: AngleOfferEntry[]
  }

  interface CampaignStructure {
    /** Per-segment angles/offers/subject_lines. Empty when 0 segments. */
    segments: SegmentStructure[]
    /** Campaign-level angles. Used ONLY when segments[].length === 0. */
    campaignAngles: AngleOfferEntry[]
    /** Campaign-level offers. Used ONLY when segments[].length === 0. */
    campaignOffers: AngleOfferEntry[]
    /** Campaign-level regions. Phase H: regions are CAMPAIGN-ONLY (Q4). */
    campaignRegions: AngleOfferEntry[]
    /**
     * Campaign-level subject_lines. Used when segments[].length === 0 or
     * when per-segment subject_lines is empty (hybrid fallback, Q6).
     */
    campaignSubjectLines: AngleOfferEntry[]
    /** Default channel for auto-created variants. Defaults to 'email'. */
    defaultChannel?: string
    /** Channels to auto-create variants for. If set, overrides defaultChannel
     *  and produces one set of variants per channel. For 'linkedin' variants,
     *  region_code and subject_line_code are skipped (LI convention: no
     *  region split, no per-send subject lines). */
    channels?: string[]
    /**
     * Per-variant overrides keyed by the 5-D cell tuple
     * `${segmentIdx}|${angleCode}|${offerCode}|${regionCode}|${subjectLineCode}`.
     * Use `-1` as segmentIdx for 0-segment campaigns. All fields optional
     * — only supplied keys override the auto-generated defaults.
     */
    variantOverrides?: Record<string, {
      name?: string
      smartlead_campaign_url?: string
      heyreach_campaign_url?: string
    }>
  }

  const createCampaign = async (campaignData: {
    name: string
    client_id: string
    description?: string
    campaign_type?: string
    version?: string
    trigger_type?: string
    channels?: string[]
    target_regions?: string[]
    tamFile?: File
    /**
     * Optional Campaign Model v2 structure. When provided:
     *  - If `segments` is empty: angles/offers go on the campaign row
     *    itself; variants are created with segment_id=NULL.
     *  - If `segments` is non-empty: each segment is inserted with its
     *    own angles/offers JSONB, and variants are auto-created for
     *    each (segment × angle × offer) cell.
     * Per-cell variant count: max(1, |angles|) × max(1, |offers|).
     */
    structure?: CampaignStructure
  }) => {
    try {
      // Extract tamFile + structure from campaignData (handled separately).
      const { tamFile, structure, ...formData } = campaignData

      // Branch the dimensions by storage level:
      //   - angles / offers: campaign-level ONLY when 0 segments (segment
      //     JSONB wins exclusively when segments exist — Phase C convention).
      //   - subject_lines: HYBRID (Phase H Q6) — campaign-level always
      //     stored as the fallback; per-segment JSONB wins when non-empty.
      //   - regions: campaign-only (Phase H Q4) — always stored at campaign.
      const usesCampaignLevel = !structure || structure.segments.length === 0
      const campaignAngles = usesCampaignLevel ? (structure?.campaignAngles ?? []) : []
      const campaignOffers = usesCampaignLevel ? (structure?.campaignOffers ?? []) : []
      const campaignSubjectLines = structure?.campaignSubjectLines ?? []
      const campaignRegions = structure?.campaignRegions ?? []

      // Clean up the data - convert empty strings to null for optional fields
      const cleanedData = {
        ...formData,
        status: 'draft', // Explicitly set to draft to prevent immediate Slack notifications
        description: formData.description?.trim() || null,
        campaign_type: formData.campaign_type?.trim() || null,
        version: formData.version?.trim() || null,
        trigger_type: formData.trigger_type?.trim() || null,
        channels: formData.channels && formData.channels.length > 0 ? formData.channels : null,
        target_regions: formData.target_regions && formData.target_regions.length > 0 ? formData.target_regions : null,
        previous_campaign_id: null, // Explicitly set to null since it's optional
        // Campaign Model v2: campaign-level dimensions. Empty arrays are
        // fine when per-segment JSONB carries them instead (angles/offers/
        // subject_lines), or when a dimension doesn't apply (regions).
        angles: campaignAngles,
        offers: campaignOffers,
        regions: campaignRegions,
        subject_lines: campaignSubjectLines,
      }

      const { data: campaignRow, error } = await supabase
        .from('campaigns')
        .insert([cleanedData])
        .select()
        .single()

      if (error) throw error

      // Phase C: auto-create segments + variants from the structure.
      if (structure) {
        await createCampaignStructure(campaignRow.id, structure)
      }

      setCampaigns(prev => [campaignRow, ...prev])
      toast({
        title: "Campaign created successfully",
        description: `${campaignData.name} has been created.`
      })

      return campaignRow
    } catch (err: any) {
      toast({
        title: "Error creating campaign",
        description: err.message,
        variant: "destructive"
      })
      throw err
    }
  }

  /**
   * Phase C + Phase H helper: insert segments + auto-generate variant
   * rows for every (segment × angle × offer × region × subject_line) cell.
   * NOT atomic — if a later insert fails, earlier rows stay (the campaign
   * exists, segments may be partial). User can clean up via the
   * EditCampaignDialog. We accept this trade-off because making it atomic
   * would require a Postgres RPC, which Sabeeh deferred.
   */
  const createCampaignStructure = async (campaignId: string, structure: CampaignStructure) => {
    // channels[] takes precedence over the legacy defaultChannel; fall back
    // to ['email'] when neither is set so existing callers don't break.
    const channels: string[] =
      structure.channels && structure.channels.length > 0
        ? structure.channels
        : [structure.defaultChannel || 'email']
    const overrides = structure.variantOverrides || {}
    const campaignRegions = structure.campaignRegions || []
    const campaignSubjectLines = structure.campaignSubjectLines || []

    // Per the Storystream / NRF rebuild convention (see handoff), LinkedIn
    // variants don't carry region or subject-line dimensions — both EU and
    // US leads are routed into the same HeyReach campaign, and HR sends
    // don't have the email-style subject-line split. Strip those
    // dimensions for the LI channel only.
    const dimsFor = (ch: string, regions: AngleOfferEntry[], subjectLines: AngleOfferEntry[]) =>
      ch === 'linkedin'
        ? { regions: [] as AngleOfferEntry[], subjectLines: [] as AngleOfferEntry[] }
        : { regions, subjectLines }

    if (structure.segments.length === 0) {
      // 0-segment campaign: variants live directly under the campaign with
      // segment_id = NULL. Auto-create one variant per cell per channel.
      const allRows: any[] = []
      for (const channel of channels) {
        const { regions, subjectLines } = dimsFor(channel, campaignRegions, campaignSubjectLines)
        const rows = buildVariantRowsForCell(
          null,
          -1,
          structure.campaignAngles,
          structure.campaignOffers,
          regions,
          subjectLines,
          channel,
          overrides,
        )
        allRows.push(...rows)
      }
      if (allRows.length === 0) return // nothing to seed
      // Phase M: stamp campaign_id on every auto-created variant.
      const stamped = allRows.map((r) => ({ ...r, campaign_id: campaignId }))
      const { error: vErr } = await supabase
        .from('ab_test_variants')
        .insert(stamped)
      if (vErr) throw vErr
      return
    }

    // 1+ segments: insert each segment, then its variants across every
    // channel. Per-segment subject_lines (hybrid, Q6) are stored on the
    // segment row; email variants use segment.subject_lines when non-empty,
    // else campaignSubjectLines as fallback. Regions are campaign-only.
    // LinkedIn variants skip regions + subject_lines regardless.
    for (let segIdx = 0; segIdx < structure.segments.length; segIdx++) {
      const seg = structure.segments[segIdx]
      const { data: segRow, error: sErr } = await supabase
        .from('ab_test_segments')
        .insert([{
          campaign_id: campaignId,
          name: seg.name.trim(),
          description: seg.description?.trim() || null,
          status: 'draft',
          angles: seg.angles,
          offers: seg.offers,
          subject_lines: seg.subject_lines,
        } as any])
        .select()
        .single()
      if (sErr) throw sErr

      const effectiveSubjectLines =
        seg.subject_lines.length > 0 ? seg.subject_lines : campaignSubjectLines
      const allRows: any[] = []
      for (const channel of channels) {
        const { regions, subjectLines } = dimsFor(channel, campaignRegions, effectiveSubjectLines)
        const rows = buildVariantRowsForCell(
          segRow.id,
          segIdx,
          seg.angles,
          seg.offers,
          regions,
          subjectLines,
          channel,
          overrides,
        )
        allRows.push(...rows)
      }
      if (allRows.length > 0) {
        // Phase M: stamp campaign_id alongside segment_id.
        const stamped = allRows.map((r) => ({ ...r, campaign_id: campaignId }))
        const { error: vErr } = await supabase
          .from('ab_test_variants')
          .insert(stamped)
        if (vErr) throw vErr
      }
    }
  }

  /**
   * Build variant rows for one cell context (one segment OR campaign-level).
   * Cell count = max(1, |angles|) × max(1, |offers|) × max(1, |regions|)
   * × max(1, |subject_lines|). When ALL four dimensions are empty, no
   * variants are created (placeholder-only isn't meaningful).
   *
   * `segmentIdx` = position in the wizard's segments array, or -1 for
   * 0-segment campaigns. Used to look up per-cell overrides.
   */
  const buildVariantRowsForCell = (
    segmentId: string | null,
    segmentIdx: number,
    angles: AngleOfferEntry[],
    offers: AngleOfferEntry[],
    regions: AngleOfferEntry[],
    subjectLines: AngleOfferEntry[],
    channel: string,
    overrides: Record<string, {
      name?: string
      smartlead_campaign_url?: string
      heyreach_campaign_url?: string
    }>,
  ) => {
    const rows: any[] = []
    const angleList = angles.length > 0 ? angles : [null as AngleOfferEntry | null]
    const offerList = offers.length > 0 ? offers : [null as AngleOfferEntry | null]
    const regionList = regions.length > 0 ? regions : [null as AngleOfferEntry | null]
    const subjectList = subjectLines.length > 0 ? subjectLines : [null as AngleOfferEntry | null]

    // Skip only when ALL four dimensions are empty — a lone placeholder
    // row isn't useful and would clutter the UI.
    const allEmpty =
      angles.length === 0 && offers.length === 0 && regions.length === 0 && subjectLines.length === 0
    if (allEmpty) return rows

    for (const angle of angleList) {
      for (const offer of offerList) {
        for (const region of regionList) {
          for (const subject of subjectList) {
            // Auto-name "Angle · Offer · Region · Subject" — each optional,
            // skipped when that dimension is null for the cell. User may
            // override via the wizard's per-variant inputs.
            const nameParts = [angle?.name, offer?.name, region?.name, subject?.name].filter(Boolean) as string[]
            const defaultName = nameParts.join(' · ') || 'Variant'
            const key = `${segmentIdx}|${angle?.code ?? ''}|${offer?.code ?? ''}|${region?.code ?? ''}|${subject?.code ?? ''}`
            const ovr = overrides[key] || {}
            // Per-channel URL dispatch: LI rows use heyreach_campaign_url
            // and auto-extract the HR campaign_id from the URL; email rows
            // keep the existing smartlead_campaign_url behaviour.
            const isLi = channel === 'linkedin'
            const url = (
              isLi ? ovr.heyreach_campaign_url : ovr.smartlead_campaign_url
            )?.trim() || ''
            // Mirror VariantDetailDialog.extractHeyreachCampaignId — try the
            // three URL shapes HeyReach uses so IDs get stamped on insert.
            const heyreachId = isLi && url
              ? (url.match(/\/campaign\/view\/(\d+)/)?.[1]
                  ?? url.match(/\/view\/(\d+)/)?.[1]
                  ?? url.match(/\/campaign\/(\d+)/)?.[1]
                  ?? null)
              : null
            rows.push({
              segment_id: segmentId,
              name: (ovr.name && ovr.name.trim()) || defaultName,
              status: 'draft',
              channel,
              angle_code: angle?.code ?? null,
              offer_code: offer?.code ?? null,
              region_code: region?.code ?? null,
              subject_line_code: subject?.code ?? null,
              // Keep parity with CreateVariantDialog: store the URL under
              // configuration.campaign_url regardless of channel. For LI
              // rows we also stamp heyreach_campaign_id when we can parse
              // it out of the URL — matches the VariantDetailDialog
              // auto-extract path so downstream HR metrics hooks can join.
              configuration: url
                ? (isLi && heyreachId
                    ? { campaign_url: url, heyreach_campaign_id: heyreachId }
                    : { campaign_url: url })
                : {},
              smartlead_campaign_id: null,
              smartlead_campaign_name: null,
              heyreach_campaign_id: heyreachId,
            })
          }
        }
      }
    }
    return rows
  }

  const deleteCampaign = async (id: string) => {
    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', id)

      if (error) throw error

      setCampaigns(prev => prev.filter(c => c.id !== id))
      toast({
        title: "Campaign deleted",
        description: "Campaign has been deleted successfully."
      })
    } catch (err: any) {
      toast({
        title: "Error deleting campaign",
        description: err.message,
        variant: "destructive"
      })
      throw err
    }
  }

  useEffect(() => {
    fetchCampaigns()
  }, [filterClientId])

  return {
    campaigns,
    loading,
    error,
    fetchCampaigns,
    createCampaign,
    deleteCampaign
  }
}